import React, { useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/organization-api';

import { useFormState } from '../../../../hooks/useFormState';
import { megabytesToBytes } from '../../../../helpers/fileUtils';

import { urlRegex } from '../../../../constants/regex_patterns';
import {
    PROVIDERS,
    CONNECTION_TYPE,
    PROVIDERS_LABEL,
    providersCardData,
} from '../../../../constants/organizationConnections';
import { defaultErrorMessage } from '../../../../constants/errorMessages';

import Modal from '../../../../design-system/Modal/Modal';
import Input from '../../../../design-system/Input/Input';
import FileInput from '../../../../design-system/FileInput/FileInput';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import FormFieldWrapper from '../../../../design-system/FormFieldWrapper/FormFieldWrapper';
import { Button } from '../../../../design-system';

const FORMATS_ARRAY = ['.json'];
const MAX_SIZE = megabytesToBytes(5);

const OrgConnectionActionModal = ({
    orgId,
    onClose,
    action = 'create', // edit | create | view
    data,
    provider,
    setOrgConnections,
    setSuccessAlertMessage = () => {},
}) => {
    const { formData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } = useFormState(
        () => {
            if ((action === 'edit' || action === 'view') && !!data) {
                return {
                    name: data.name,
                    weaviate_url: data.detail?.weaviate_url,
                    api_key: '',
                    file: null,
                };
            }
            return {
                name: '',
                api_key: '',
                weaviate_url: '',
                file: null,
            };
        }
    );

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const { connectionType } = providersCardData[provider] || {};

    const readJsonFile = (file) => {
        const showErrorMessage = (message) => {
            setFieldErrorMessages((prev) => ({ ...prev, file: message }));
        };

        return new Promise((resolve, reject) => {
            if (file && file.type === 'application/json') {
                const reader = new FileReader();

                reader.onload = (e) => {
                    try {
                        const parsedJson = JSON.parse(e.target.result);
                        const api_key = parsedJson.private_key;
                        const detail = { ...parsedJson, private_key: '' };
                        resolve({ detail, api_key });
                    } catch (error) {
                        showErrorMessage(
                            'Invalid JSON file. Please upload a .json file containing the key information.'
                        );
                        reject(new Error('Invalid JSON file'));
                    }
                };

                reader.onerror = () => {
                    showErrorMessage('Please upload a .json file containing the key information.');
                    reject(new Error('Failed to read the file'));
                };

                reader.readAsText(file);
            } else {
                showErrorMessage('Please upload a .json file containing the key information.');
                reject(new Error('Please upload a valid JSON file'));
            }
        });
    };

    const createConnection = async () => {
        const { name, api_key, weaviate_url, file } = formData;

        const requestBody = { name, organization: orgId, provider };

        if (connectionType !== CONNECTION_TYPE.private_key_file) {
            requestBody.api_key = api_key;
        }

        if (connectionType === CONNECTION_TYPE.private_key_file) {
            const { detail, api_key } = await readJsonFile(file);
            requestBody.api_key = api_key;
            requestBody.detail = detail;
        }

        if (provider === PROVIDERS.weaviate) {
            requestBody.detail = { weaviate_url: weaviate_url.trim() };
        }

        const { data: response } = await client.post(
            API.ROUTES.organization.connection,
            requestBody
        );
        setOrgConnections && setOrgConnections((prevData) => [...prevData, response]);
        setSuccessAlertMessage(`Successfully connected to ${PROVIDERS_LABEL[provider]}`);
    };

    const editConnection = async () => {
        const { name, api_key, weaviate_url, file } = formData;
        const requestBody = { name };

        if (!!api_key && connectionType !== CONNECTION_TYPE.private_key_file) {
            requestBody.api_key = api_key;
        }

        if (!!file && connectionType === CONNECTION_TYPE.private_key_file) {
            const { detail, api_key } = await readJsonFile(file);
            requestBody.api_key = api_key;
            requestBody.detail = detail;
        }

        if (!!weaviate_url && provider === PROVIDERS.weaviate) {
            requestBody.detail = { weaviate_url: weaviate_url.trim() };
        }

        const { data: response } = await client.patch(
            `${API.ROUTES.organization.connection}${data.id}/`,
            requestBody
        );
        setOrgConnections &&
            setOrgConnections((prevData) =>
                prevData.map((item) => (item.id === response.id ? response : item))
            );
    };

    const handleAction = {
        create: createConnection,
        edit: editConnection,
    };

    const handleConfirm = async (e) => {
        e.preventDefault();

        if (action === 'view') return;

        if (errorMessage) {
            setErrorMessage(null);
        }

        const errors = getValidationErrors();
        const hasErrors = Object.values(errors).some(Boolean);

        if (hasErrors) {
            setFieldErrorMessages(errors);
            return;
        }

        try {
            setIsLoading(true);

            await handleAction[action]();

            setIsLoading(false);
            onClose();
        } catch (error) {
            setIsLoading(false);
            if (
                error.response &&
                (error.response.data?.name ||
                    error.response.data?.api_key ||
                    error.response.data?.weaviate_url)
            ) {
                setFieldErrorMessages({
                    name: error.response.data?.name ? error.response.data?.name[0] : null,
                    api_key: error.response.data?.api_key ? error.response.data?.api_key[0] : null,
                    weaviate_url: error.response.data?.weaviate_url
                        ? error.response.data?.weaviate_url[0]
                        : null,
                });
            } else if (error.response && error.response.data?.non_field_errors) {
                setErrorMessage(error.response.data.non_field_errors[0]);
            }
            setErrorAlert({
                message: defaultErrorMessage,
                statusCode: error.response?.status,
            });
        }
    };

    function getValidationErrors() {
        const { name, weaviate_url, api_key, file } = formData;
        const isUrlValid = urlRegex.test(weaviate_url?.trim());

        const commonErrors = {
            name: !name ? 'This field may not be blank.' : '',
            weaviate_url:
                provider === PROVIDERS.weaviate
                    ? !weaviate_url
                        ? 'This field may not be blank.'
                        : !isUrlValid
                        ? 'Please check the entered URL as it seems to be invalid.'
                        : ''
                    : '',
        };

        if (action === 'create') {
            return {
                ...commonErrors,
                api_key:
                    connectionType !== CONNECTION_TYPE.private_key_file && !api_key
                        ? 'This field may not be blank.'
                        : '',
                file:
                    connectionType === CONNECTION_TYPE.private_key_file && !file
                        ? 'Please upload a .json file containing the key information.'
                        : '',
            };
        }

        return commonErrors;
    }

    const getInputState = (field) => {
        return action === 'view' ? 'disabled' : fieldErrorMessages[field] ? 'error' : 'default';
    };

    return (
        <Modal onClose={onClose} size="medium">
            <div className="flex flex-col gap-4">
                <h2 className="font-heading-bold text-heading-bold-m text-black">
                    {action === 'edit'
                        ? `Update your ${PROVIDERS_LABEL[provider]} Connection`
                        : `Connect your ${PROVIDERS_LABEL[provider]} Account`}
                </h2>
                <Input
                    label="Name"
                    value={formData.name}
                    placeholder={`My ${PROVIDERS_LABEL[provider]} Key`}
                    name="name"
                    size="md"
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    isRequired
                    state={getInputState('name')}
                    errorMessage={fieldErrorMessages.name}
                />
                {provider === PROVIDERS.weaviate && (
                    <Input
                        label={`${PROVIDERS_LABEL[provider]} URL`}
                        value={formData.weaviate_url}
                        placeholder={`https://test-xxxxxx.${provider}.network`}
                        name="weaviate_url"
                        size="md"
                        onChange={(e) => handleInputChange('weaviate_url', e.target.value)}
                        isRequired
                        state={getInputState('weaviate_url')}
                        errorMessage={fieldErrorMessages.weaviate_url}
                    />
                )}
                {connectionType !== CONNECTION_TYPE.private_key_file && (
                    <Input
                        label={`${PROVIDERS_LABEL[provider]} API key`}
                        value={formData.api_key}
                        placeholder={action === 'create' ? 'xxxxxxxx' : '********************'}
                        name="api_key"
                        size="md"
                        isRequired
                        state={getInputState('api_key')}
                        errorMessage={fieldErrorMessages.api_key}
                        onChange={(e) => handleInputChange('api_key', e.target.value)}
                    />
                )}

                {connectionType === CONNECTION_TYPE.private_key_file && (
                    <FormFieldWrapper
                        label={`${PROVIDERS_LABEL[provider]} File Upload`}
                        isRequired
                        state={getInputState('file')}
                        errorMessage={fieldErrorMessages.file}
                        tipText="Upload a .json file containing the key information"
                        topRightTipText="Max: 5 MB"
                    >
                        <FileInput
                            view="simple"
                            placeholder="Drag & drop a file to upload or click to browse"
                            file={formData.file}
                            formatsArray={FORMATS_ARRAY}
                            maxSizeInBytes={MAX_SIZE}
                            state={getInputState('file')}
                            handleFile={(file) => handleInputChange('file', file)}
                        />
                    </FormFieldWrapper>
                )}

                {errorMessage && (
                    <p className="font-body text-body-bold-xs text-red-500">{errorMessage}</p>
                )}

                <div className="flex items-center gap-2 justify-between pt-2">
                    <Button type="neutral" size="xs" text="Cancel" onClick={onClose} />
                    <Button
                        size="xs"
                        type="primary"
                        text="Confirm"
                        state={action === 'view' ? 'disabled' : isLoading ? 'loading' : 'default'}
                        onClick={handleConfirm}
                    />
                </div>
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

export default OrgConnectionActionModal;
